import { useState, type ReactElement } from "react";

import { Button } from "@/components/ui/button";

// Owner-facing display of a private site's preview password: masked by default,
// with reveal + copy. New sites launch private (password-gated); the owner needs
// this to view their own site and to share a preview. See the CloudFront gate in
// infra/tenant-router-function.ts.

export function unlockUrl(siteUrl: string, password: string): string {
  // The tenant router accepts ?pw=<password>, sets the `cp` cookie, then 302s to
  // a clean URL — so the password isn't left in the address bar.
  const u = new URL(siteUrl);
  u.searchParams.set("pw", password);
  return u.toString();
}

export function PreviewPassword({
  password,
}: {
  password: string;
}): ReactElement {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / permissions) — reveal instead so
      // the owner can copy manually.
      setRevealed(true);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm tracking-wider">
        {revealed ? password : "•".repeat(Math.max(password.length, 8))}
      </code>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? "Hide password" : "Reveal password"}
      >
        {revealed ? "Hide" : "Reveal"}
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={copy}>
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}
